import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PERMISSION_NAME, PermissionService } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { debounceTime, filter, firstValueFrom, Subscription, switchMap } from 'rxjs';
import { CavoRaccordo } from '../../../../models/cavo-raccordo.model';
import { FeatureSelectService } from '../../../../services/feature-select.service';
import { RaccordoService } from '../../../../services/raccordo.service';
import { TelcoService } from '../../../../services/telco.service';
import { FormLayersModalComponent } from '../../dynamic-layers/form-layers-modal/form-layers-modal.component';

// FAS-1579: valore di potenzialità IRU dei cavi proprietari
const POTENZIALITA_IRU_ND = 'ND';

@Component({
  selector: 'ebw-monorepo-raccordi-cavo-edit',
  templateUrl: './raccordi-cavo-edit.component.html',
  styleUrls: ['./raccordi-cavo-edit.component.scss']
})
export class RaccordiCavoEditComponent implements OnInit {
  public isNew = false;
  public isEditable = true;
  public raccordo: CavoRaccordo;
  public form: FormGroup;
  autocompleteSubscription: Subscription;
  searchResultsBuildings: any[];

  // La potenzialità IRU non può superare la potenzialità del cavo
  private _iruMaxValidator = (control: AbstractControl): ValidationErrors | null => {
    const iru = Number(control.value);
    const cavo = Number(control.parent?.get('potenzialita_cavo')?.value);
    return control.value && cavo && iru > cavo ? { iruMax: true } : null;
  };

  // ---------------------------------------------------------------------------------

  constructor(
    public dialogRef: MatDialogRef<FormLayersModalComponent>,
    private _raccordiService: RaccordoService,
    private _fb: FormBuilder,
    private _translateService: TranslateService,
    private _ebwModal: EbwModalService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _permissionService: PermissionService,
    private _telcoService: TelcoService,
    private _featureSelectService: FeatureSelectService
  ) {}

  // ---------------------------------------------------------------------------------

  async ngOnInit(): Promise<void> {
    this.isEditable =
      this._permissionService.checkPermission(PERMISSION_NAME.FIBER_JOINTS.RACCORDI_SALE_EDIT_WEB) ||
      this._permissionService.checkPermission(PERMISSION_NAME.FIBER_JOINTS.RACCORDI_SALE_CREATE);
    this.isNew = this.data.id == null;
    this.isNew ? this._initForm() : this._fetchData();
  }

  // ---------------------------------------------------------------------------------

  public async onSubmit() {
    if (this.form.invalid) {
      return;
    }

    if (!this.form.get('nome_edificio').value) {
      const title = 'Attenzione';
      const content = `Il nome dell'edificio è obbligatorio`;
      this._ebwModal.openErrorModal({ title, content });
      return;
    }

    let value: CavoRaccordo = this.form.getRawValue();
    value.data_verbale = moment(value.data_verbale).format('DD/MM/YYYY');

    let mappedValue: any = { ...this.raccordo, ...value };
    mappedValue = this._removeNullValues(mappedValue);
    // rimuovo il campo usato per la ricerca e quello usato solo per valorizzare la potenzialità IRU
    delete mappedValue.building;
    delete mappedValue.cavo_proprietario;

    this._save(mappedValue);
  }

  // ---------------------------------------------------------------------------------

  private _initForm(raccordo?: CavoRaccordo) {
    if (this.form) {
      this.form.reset();
    }

    this.form = this._fb.group({
      id: [raccordo?.id || null],
      building: [''],
      nome_edificio: [raccordo?.nome_edificio || null, Validators.required],
      latitudine: [raccordo?.latitudine || null],
      longitudine: [raccordo?.longitudine || null],
      alias: [raccordo?.alias || null],
      gat_sede: [raccordo?.gat_sede || null],
      data_verbale: [raccordo?.data_verbale ? moment(raccordo.data_verbale, 'DD/MM/YYYY').toDate() : new Date()],
      codice_fattibilita: [raccordo?.codice_fattibilita || null],
      td_raccordo: [raccordo?.td_raccordo || null],
      nome_cavo: [raccordo?.nome_cavo || null],
      sala_1: [raccordo?.sala_1 || null, Validators.required],
      sala_2: [raccordo?.sala_2 || null],
      terminazione_1: [raccordo?.terminazione_1 || null, Validators.required],
      terminazione_2: [raccordo?.terminazione_2 || null],
      potenzialita_cavo: [
        raccordo?.potenzialita_cavo ?? null,
        [Validators.required, Validators.min(1), Validators.max(1728)]
      ],
      potenzialita_iru: [
        raccordo?.potenzialita_iru ?? null,
        [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1), this._iruMaxValidator]
      ],
      // usato solo in creazione: se flaggato la potenzialità IRU vale ND
      cavo_proprietario: [false]
    });

    if (this.isNew) {
      // la potenzialità IRU dipende da quella del cavo: ricalcolo la validità quando cambia
      this.form
        .get('potenzialita_cavo')
        .valueChanges.subscribe(() => this.form.get('potenzialita_iru').updateValueAndValidity());
      this.form.get('cavo_proprietario').valueChanges.subscribe((proprietario: boolean) => {
        const iru = this.form.get('potenzialita_iru');
        if (proprietario) {
          iru.setValue(POTENZIALITA_IRU_ND);
          iru.disable();
        } else {
          iru.enable();
          iru.setValue(null);
        }
      });
    }

    // FAS-1475
    // Una volta creato il Cavo:
    // - Potenzialità Cavo: campo readonly/greyato, non più modificabile dall'utente.
    // - Potenzialità IRU: campo readonly/greyato, campo dedotto.
    if (!this.isNew) {
      this.form.get('potenzialita_cavo').disable();
      this.form.get('potenzialita_iru').disable();
    }

    // Disabilito i campi che vengono popolati dall'autocomplete dell'edificio
    this.form.get('nome_edificio').disable();
    this.form.get('alias').disable();
    this.form.get('gat_sede').disable();

    const isCavoVirtuale = !raccordo?.id_gis;
    // Se il cavo è importato da PNI, devono essere modificabili solo i campi TD Raccordo e Codice Fattibilità
    if (!isCavoVirtuale) {
      this.form.get('id').disable();
      this.form.get('data_verbale').disable();
      this.form.get('nome_cavo').disable();
      this.form.get('sala_1').disable();
      this.form.get('sala_2').disable();
      this.form.get('terminazione_1').disable();
      this.form.get('terminazione_2').disable();
      this.form.get('potenzialita_cavo').disable();
      this.form.get('potenzialita_iru').disable();
    }
    // Se siamo in modifica non permetto di cambiare l'edificio
    if (!this.isNew) {
      this.form.get('building').disable();
    }
    // Se provengo dal dettaglio di un edificio non permetto di cambiare l'edificio
    if (this.data.urn) {
      this.form.get('building').disable();
      this.onSelectBuilding(this.data.urn);
    }

    this.setupAutocomplete();
  }

  // ---------------------------------------------------------------------------------

  private async _getLatestRaccordo(id) {
    try {
      const result = await firstValueFrom(this._raccordiService.getRaccordo(id));

      return safeJsonClone(result);
    } catch (error) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------------

  private async _fetchData() {
    const raccordoTmp: CavoRaccordo = await this._getLatestRaccordo(this.data.id);

    if (raccordoTmp) {
      this.raccordo = raccordoTmp;
      this._initForm(raccordoTmp);
    }
  }

  // ---------------------------------------------------------------------------------
  private _save(data: CavoRaccordo) {
    const observable = data.id ? this._raccordiService.updateRaccordo(data) : this._raccordiService.addRaccordo(data);

    observable
      .pipe(
        switchMap(() => {
          const title = this._translateService.instant('common.save_successful');
          return this._ebwModal.openSuccessModal({ title, content: '' }).afterClosed();
        })
      )
      .subscribe({
        next: () => this.dialogRef.close(true),

        error: (error) => {
          const title = this._translateService.instant('common.error');
          const content = error?.error?.message ?? this._translateService.instant('common.error_saving');
          this._ebwModal.openErrorModal({ title, content });
        }
      });
  }

  // ------------------------------------------------------------------------------------------------

  private _removeNullValues(obj) {
    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== null) {
        result[key] = obj[key];
      }
    }
    return result;
  }

  // ------------------------------------------------------------------------------------------------

  private setupAutocomplete(): void {
    if (this.autocompleteSubscription) {
      return;
    }
    // mi sottoscrivo ai cambiamenti dell'autocomplete per avviare la ricerca su solr
    this.autocompleteSubscription = this.form
      .get('building')
      .valueChanges.pipe(
        debounceTime(300),
        filter((value: string) => value?.length > 2),
        switchMap((value: string) => this._telcoService.caviRaccordoSearch(value))
      )
      .subscribe({
        next: (results: any[]) => {
          this.searchResultsBuildings = results ?? [];
        },
        error: (error) => {
          // setto eventuali errori e pulisco l'array
          console.error('[RaccordoCavoEdit][setupAutocomplete]', error);
          this.searchResultsBuildings = [];
          this.form.get('building').setErrors({ solrError: true });
          this.autocompleteSubscription.unsubscribe();
          this.setupAutocomplete();
        }
      });
  }

  // ------------------------------------------------------------------------------------------------

  async onSelectBuilding(urn: string) {
    try {
      const { result: response } = await this._featureSelectService.selectByUrns([urn]);
      const feature = response.features[0];
      const properties = feature.properties;
      const [lon, lat] = feature.geometry.coordinates;

      const nameField = this.form.get('nome_edificio');
      nameField.enable();
      nameField.setValue(properties.name);
      nameField.disable();

      const latField = this.form.get('latitudine');
      latField.setValue(lat);

      const lonField = this.form.get('longitudine');
      lonField.setValue(lon);

      const aliasField = this.form.get('alias');
      aliasField.enable();
      aliasField.setValue(properties.alias ?? '');
      aliasField.disable();

      const gatField = this.form.get('gat_sede');
      gatField.enable();
      gatField.setValue(properties.gat ?? '');
      gatField.disable();
    } catch (error) {
      console.error('[RaccordoCavoEdit][onSelectBuilding]', error);
    }
  }
}
